import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, MailWarning, RotateCcw, Search, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { StatusBadge } from "@/pages/admin/AdminOverview";

const STATUSES = ["New", "Contacted", "Qualified", "Converted", "Closed"];

const DetailRow = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-brand-mist">{label}</p>
    <p className="mt-0.5 text-sm text-brand-snow">{value || "—"}</p>
  </div>
);

const EnquiryDetail = ({ enquiry, onClose, onChanged }) => {
  const [retrying, setRetrying] = useState(false);
  const [emailStatus, setEmailStatus] = useState(enquiry.email_status || {});
  const [notice, setNotice] = useState("");

  const setStatus = async (status) => {
    await api.patch(`/admin/enquiries/${enquiry.enquiry_id}/status`, { status });
    onChanged(enquiry.enquiry_id, status);
  };

  const retry = async () => {
    setRetrying(true);
    setNotice("");
    try {
      const r = await api.post(`/admin/enquiries/${enquiry.enquiry_id}/retry-email`);
      setEmailStatus(r.data.email_status);
      setNotice("Retry attempted.");
    } catch (err) {
      setNotice(formatApiError(err.response?.data?.detail));
    }
    setRetrying(false);
  };

  const anyFailed = Object.values(emailStatus).includes("failed");

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-brand-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} data-testid="enquiry-detail-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-brand-mist/15 bg-brand-charcoal p-6 sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">{enquiry.enquiry_id}</p>
            <h2 className="mt-1 font-display text-xl font-bold text-brand-snow">{enquiry.full_name}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="enquiry-detail-close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow">
            <X size={18} />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-3">
          <DetailRow label="Email" value={enquiry.email} />
          <DetailRow label="Phone" value={enquiry.phone} />
          <DetailRow label="Company" value={enquiry.company} />
          <DetailRow label="Country" value={enquiry.country} />
          <DetailRow label="Service" value={enquiry.service_name} />
          <DetailRow label="Budget" value={enquiry.budget} />
          <DetailRow label="Timeline" value={enquiry.timeline} />
          <DetailRow label="Source page" value={enquiry.source_page} />
          <DetailRow label="Submitted (UTC)" value={new Date(enquiry.created_at).toLocaleString("en-AU", { timeZone: "UTC" })} />
        </div>

        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-wider text-brand-mist">Project details</p>
          <p className="mt-2 whitespace-pre-wrap rounded-lg border border-brand-mist/12 bg-brand-black p-4 text-sm leading-relaxed text-brand-snow" data-testid="enquiry-detail-message">
            {enquiry.message}
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="enquiry-status-actions">
          <span className="mr-1 text-xs text-brand-mist">Set status:</span>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`enquiry-set-${s.toLowerCase()}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${enquiry.status === s ? "border-brand-emerald bg-brand-emerald/15 text-brand-emerald" : "border-brand-mist/20 text-brand-mist hover:border-brand-emerald/50 hover:text-brand-snow"}`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-3 border-t border-brand-mist/10 pt-5 text-xs text-brand-mist">
          <MailWarning size={14} className={anyFailed ? "text-red-300" : "text-brand-emerald"} />
          <span data-testid="enquiry-email-status">
            Admin: {emailStatus.admin || "pending"} · Customer: {emailStatus.customer || "pending"}
          </span>
          {anyFailed && (
            <button onClick={retry} disabled={retrying} data-testid="enquiry-retry-email" className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-brand-azure/40 px-3 py-1.5 text-xs font-medium text-brand-azure hover:bg-brand-azure/10 disabled:opacity-50">
              <RotateCcw size={12} /> {retrying ? "Retrying…" : "Retry emails"}
            </button>
          )}
        </div>
        {notice && <p className="mt-2 text-xs text-brand-mist" data-testid="enquiry-retry-notice">{notice}</p>}
      </div>
    </div>
  );
};

const AdminEnquiries = () => {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ total: 0, items: [] });
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [service, setService] = useState("");
  const [open, setOpen] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status) p.set("status", status);
    if (service) p.set("service", service);
    p.set("limit", "100");
    api
      .get(`/admin/enquiries?${p.toString()}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail)));
  }, [q, status, service]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/services?limit=200").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const openId = params.get("open");
    if (openId) {
      api.get(`/admin/enquiries/${openId}`).then((r) => setOpen(r.data)).catch(() => {});
    }
  }, [params]);

  const onChanged = (id, newStatus) => {
    setData((d) => ({ ...d, items: d.items.map((e) => (e.enquiry_id === id ? { ...e, status: newStatus } : e)) }));
    setOpen((o) => (o ? { ...o, status: newStatus } : o));
  };

  const selectCls = "rounded-md border border-brand-mist/20 bg-brand-charcoal px-3 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none";

  return (
    <div className="space-y-6" data-testid="admin-enquiries">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Enquiries</h1>
          <p className="text-sm text-brand-mist">{data.total} record{data.total === 1 ? "" : "s"}</p>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/enquiries/export.csv`}
          data-testid="enquiries-export"
          className="inline-flex items-center gap-2 rounded-full border border-brand-mist/25 px-4 py-2.5 text-sm font-medium text-brand-snow transition-colors hover:border-brand-emerald hover:text-brand-emerald"
        >
          <Download size={15} /> Export CSV
        </a>
      </div>

      <div className="flex flex-wrap gap-3" data-testid="enquiry-filters">
        <div className="relative min-w-56 flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mist" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, company or ID…"
            data-testid="enquiry-search"
            className="w-full rounded-md border border-brand-mist/20 bg-brand-charcoal py-2.5 pl-9 pr-3 text-sm text-brand-snow placeholder:text-brand-mist/50 focus:border-brand-emerald focus:outline-none"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="enquiry-filter-status" className={selectCls}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={service} onChange={(e) => setService(e.target.value)} data-testid="enquiry-filter-service" className={selectCls}>
          <option value="">All services</option>
          {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
        </select>
      </div>

      {error && <p className="text-sm text-red-400" data-testid="enquiries-error">{error}</p>}

      <div className="surface-card overflow-x-auto" data-testid="enquiries-table">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-brand-mist/10 text-xs uppercase tracking-wider text-brand-mist">
              <th className="px-5 py-3.5 font-medium">Enquiry</th>
              <th className="px-5 py-3.5 font-medium">Contact</th>
              <th className="px-5 py-3.5 font-medium">Service</th>
              <th className="px-5 py-3.5 font-medium">Date</th>
              <th className="px-5 py-3.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-mist/8">
            {data.items.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-brand-mist">No enquiries match.</td></tr>
            )}
            {data.items.map((e) => (
              <tr
                key={e.enquiry_id}
                onClick={() => api.get(`/admin/enquiries/${e.enquiry_id}`).then((r) => setOpen(r.data))}
                data-testid={`enquiry-row-${e.enquiry_id}`}
                className="cursor-pointer transition-colors hover:bg-white/5"
              >
                <td className="px-5 py-4">
                  <p className="font-medium text-brand-snow">{e.full_name}</p>
                  <p className="text-xs text-brand-mist">{e.enquiry_id}</p>
                </td>
                <td className="px-5 py-4">
                  <p className="text-brand-snow">{e.email}</p>
                  <p className="text-xs text-brand-mist">{e.country}</p>
                </td>
                <td className="px-5 py-4 text-brand-mist">{e.service_name}</td>
                <td className="px-5 py-4 text-brand-mist">{new Date(e.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</td>
                <td className="px-5 py-4"><StatusBadge status={e.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <EnquiryDetail
          enquiry={open}
          onClose={() => { setOpen(null); params.delete("open"); setParams(params, { replace: true }); }}
          onChanged={onChanged}
        />
      )}
    </div>
  );
};

export default AdminEnquiries;
